import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { api, parseError } from "../lib/api";
import { usePaged, LoadMore, useDebounced } from "../lib/paged";
import { fmtDate } from "../lib/format";
import { apiFail } from "../lib/p2";
import { Btn, Card, Input, Notice, PageTitle, Select, Tag, DemoBadge, SampleBadge } from "../components/kit";
import { Loading, NoAccess } from "../components/common";
import { PublicHeader } from "../components/PublicHeader";
import { PublicFooter } from "../components/PublicFooter";
import ProductCard, { uiLang, tv, ContextTag } from "../components/ProductCard";
import { Band, Cta, CtaBand, EdShell, Hero, IMG } from "../components/Editorial";
import { ExploreDemo } from "../components/PublicHeader";

const EMPTY = { brand: "", category: "", coat_type: "", purpose: "", q: "" };

function Filters({ f, setF, facets }) {
  const { t } = useTranslation();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const sel = (k, ns, opts, all) => (
    <Select aria-label={t(`cat.f_${k}`)} data-testid={`catalogue-filter-${k}`} value={f[k]} onChange={set(k)}>
      <option value="">{t(all)}</option>{(opts || []).map((o) => <option key={o} value={o}>{ns ? tv(t, ns, o) : o}</option>)}
    </Select>
  );
  return (
    <Card className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="catalogue-filters">
      <label className="relative lg:col-span-1"><span className="sr-only">{t("cat.search")}</span><Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" aria-hidden />
        <Input className="pl-9" data-testid="catalogue-search" placeholder={t("cat.search")} value={f.q} onChange={set("q")} /></label>
      {sel("brand", null, facets?.brands, "cat.all_brands")}
      {sel("category", "category", facets?.categories, "cat.all_categories")}
      {sel("coat_type", "coat", facets?.coat_types, "cat.all_coats")}
      {sel("purpose", "purpose", facets?.purposes, "cat.all_purposes")}
    </Card>
  );
}

const shownName = (p, lang) => (p.lang_status?.[lang] === "human_reviewed" && p.editions?.[lang]?.name) || p.name;

function CatalogueBrowse({ listUrl, facetsUrl, linkBase, testid }) {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n);
  const [f, setF] = useState(EMPTY);
  const [facets, setFacets] = useState(null);
  const q = useDebounced(f.q);
  const params = { ...Object.fromEntries(Object.entries({ ...f, q }).filter(([, v]) => v)), lang };
  const pg = usePaged(listUrl, params, 12);
  useEffect(() => { api.get(facetsUrl).then((r) => setFacets(r.data)).catch(() => setFacets({})); }, [facetsUrl]);
  return (
    <div className="space-y-4" data-testid={testid}>
      {facets?.sample_present && <Notice testid="catalogue-sample-notice">{t("b4.sample")}</Notice>}
      <Filters f={f} setF={setF} facets={facets} />
      <p className="text-sm text-muted-foreground" data-testid="catalogue-total">{t("cat.results", { count: pg.total || 0 })}</p>
      {!pg.items ? <Loading /> : pg.items.length === 0 ? <Notice testid="catalogue-empty">{t("cat.none")}</Notice> : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalogue-list">
          {pg.items.map((p) => (
            <li key={p.id}><Link to={`${linkBase}/${p.id}`} data-testid={`catalogue-item-${p.number}`} className="lift block h-full overflow-hidden rounded-2xl border bg-white">
              {linkBase === "/products" && <figure className="relative"><img src={p.is_demo ? "/img/b8/catalogue.webp" : "/img/b9/product_illustration.webp"} alt="" aria-hidden loading="lazy" className="aspect-[16/9] w-full bg-ivory object-cover" />
                <figcaption className="absolute bottom-2 left-2 rounded-full bg-card/90 px-2 py-0.5 text-xs font-semibold text-neutral-ink" data-testid={`catalogue-visual-${p.number}`}>{t(p.is_demo ? "b9.example_photo" : "b9.illustration")}</figcaption></figure>}
              <div className="p-4">
              <div className="flex flex-wrap items-center gap-2">{p.sample ? <SampleBadge testid={`sample-badge-${p.number}`} /> : p.is_demo && <DemoBadge short />}{p.category && <Tag>{tv(t, "category", p.category)}</Tag>}{p.use_context && p.use_context !== "both" && <ContextTag ctx={p.use_context} />}</div>
              <h2 className="mt-2 break-words text-base font-bold">{p.edition ? p.name : shownName(p, lang)}</h2>
              <p className="text-sm text-muted-foreground">{p.brand}</p>
              {(p.lang_notice || (p.lang_status && p.lang_status[lang] !== "human_reviewed")) && <p className="mt-1 text-xs font-semibold text-amber-ink">{t(`cat.lang_short_${p.lang_notice || p.lang_status[lang]}`, { lang: t(`lang.${lang}`) })}</p>}
              </div>
            </Link></li>
          ))}
        </ul>
      )}
      <LoadMore shown={(pg.items || []).length} total={pg.total} onMore={pg.more} loading={pg.loading} testid="catalogue" />
    </div>
  );
}

function PublicShell({ children }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen grain" data-testid="public-catalogue-page">
      <PublicHeader ids={{ catalogue: "public-catalogue-home" }} />
      <main className="mx-auto max-w-5xl px-4 pb-16 pt-6 sm:px-6 sm:pt-10">{children}</main>
      <PublicFooter />
    </div>
  );
}

export function PublicCatalogue() {
  const { t } = useTranslation();
  return <EdShell testid="public-catalogue-page"><Hero testid="catalogue-hero" eyebrow={t("b8.cat_eyebrow")} title={t("cat.title")} lead={t("cat.public_sub")} img={IMG("catalogue")} alt={t("b8.catalogue_alt")}
    ctas={<><Cta primary to="/register" testid="catalogue-cta-start">{t("b8.start_learning")}</Cta><ExploreDemo testid="catalogue-cta-demo" className="max-sm:whitespace-normal" /></>} />
    <Band testid="catalogue-band"><CatalogueBrowse listUrl="/public/catalogue" facetsUrl="/public/catalogue-facets" linkBase="/catalogue" testid="public-catalogue" /></Band><CtaBand /></EdShell>;
}

function useProduct(url) {
  const { i18n } = useTranslation();
  const lang = uiLang(i18n);
  const [p, setP] = useState(null);
  const [denied, setDenied] = useState(false);
  const seq = useRef(0);
  const load = () => {
    const n = ++seq.current;
    return api.get(url, { params: { lang } }).then((r) => n === seq.current && setP(r.data)).catch((e) => n === seq.current && parseError(e).status === 404 && setDenied(true));
  };
  useEffect(() => { load(); }, [url, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  return [p, denied, load];
}

export function PublicProduct() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [p, denied] = useProduct(`/public/catalogue/${id}`);
  return <PublicShell>{denied ? <Notice tone="warn" testid="public-product-not-found">{t("cat.not_found")} <Link to="/catalogue" className="inline-flex min-h-[44px] items-center font-semibold underline" data-testid="back-to-catalogue">{t("cat.back_to_catalogue")}</Link></Notice> : !p ? <Loading /> : <><PageTitle title={p.name} sub={p.brand} /><ProductCard p={p} linkBase="/catalogue" /></>}</PublicShell>;
}

export function AppCatalogue() {
  const { t } = useTranslation();
  return <div><PageTitle title={t("cat.title")} sub={t("cat.app_sub")} /><div className="mt-4"><CatalogueBrowse listUrl="/products" facetsUrl="/catalogue-facets" linkBase="/products" testid="app-catalogue" /></div><PrivateProducts /></div>;
}

function PrivateProducts() {
  const { t } = useTranslation();
  const pg = usePaged("/products-mine", {}, 20);
  if (!pg.items?.length) return null;
  return (
    <Card className="mt-8" data-testid="private-products"><h2 className="text-base font-bold md:text-lg">{t("cat.private_title")}</h2><p className="mb-3 text-sm text-muted-foreground">{t("cat.private_sub")}</p>
      <ul className="space-y-2">{pg.items.map((p) => <li key={p.id}><Link to={`/products/${p.id}`} className="flex min-h-[44px] flex-wrap items-center gap-2 rounded-xl border bg-white px-3 py-2 text-sm" data-testid={`private-product-${p.id}`}><b>{p.name}</b>{p.submission && <Tag>{t(`cat.sub_${p.submission.status}`)}</Tag>}</Link></li>)}</ul>
      <LoadMore shown={pg.items.length} total={pg.total} onMore={pg.more} loading={pg.loading} testid="private-products" /></Card>
  );
}

function MyUse({ id }) {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const load = (offset) => api.get(`/products/${id}/my-use`, { params: { offset, limit: 20 } })
    .then((r) => setD((prev) => (offset && prev ? { ...r.data, recommended: [...prev.recommended, ...r.data.recommended], used: [...prev.used, ...r.data.used] } : r.data)))
    .catch(() => setD({ recommended: [], used: [], recommended_total: 0, used_total: 0 }));
  useEffect(() => { load(0); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <Loading />;
  const more = d.recommended_total > d.recommended.length || d.used_total > d.used.length;
  const nextOffset = Math.max(d.recommended.length, d.used.length);
  const L = "inline-flex min-h-[44px] items-center font-semibold underline";
  return (
    <section className="space-y-4" data-testid="my-use">
      <h2 className="text-base font-bold md:text-lg">{t("cat.my_use")}</h2>
      <Card><h3 className="mb-2 font-bold">{t("cat.recommended_in")}</h3>{d.recommended.length === 0 ? <p className="text-sm text-muted-foreground">{t("cat.none_yet")}</p> :
        <ul className="space-y-1 text-sm" data-testid="my-use-recommended">{d.recommended.map((r) => <li key={r.programme_id} className="flex flex-wrap items-center gap-2"><Link className={L} to={`/programmes/${r.programme_id}`}>{r.number}</Link> · {r.dog_name} · {fmtDate(r.date)} {r.contexts.map((c) => <ContextTag key={c} ctx={c} />)}</li>)}</ul>}</Card>
      <Card><h3 className="mb-2 font-bold">{t("cat.used_in")}</h3>{d.used.length === 0 ? <p className="text-sm text-muted-foreground">{t("cat.none_yet")}</p> :
        <ul className="space-y-3 text-sm" data-testid="my-use-used">{d.used.map((u) => (
          <li key={u.visit_id} className="rounded-xl border bg-white p-3"><p><Link className={L} to={`/visits/${u.visit_id}`}>{u.number}</Link> · {u.dog_name} · {fmtDate(u.date)} · {u.actions.join(", ")}</p>
            <p className="mt-2 text-xs font-bold uppercase tracking-wide text-muted-foreground" data-testid="observations-after-label">{t("cat.obs_after")}</p>
            {u.observations_after.length === 0 ? <p className="text-muted-foreground">{t("cat.none_yet")}</p> : <ul className="mt-1 space-y-1">{u.observations_after.map((o) => <li key={o.id}>{fmtDate(o.event_date)} · {o.number} · {o.region} — {o.sign}</li>)}</ul>}</li>))}</ul>}</Card>
      {more && <Btn variant="outline" testid="my-use-more" onClick={() => load(nextOffset)}>{t("cat.show_more")}</Btn>}
    </section>
  );
}

export function AppProduct() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [p, denied, reload] = useProduct(`/products/${id}/card`);
  if (denied) return <NoAccess />;
  if (!p) return <Loading />;
  if (p.private) return (
    <div className="mx-auto max-w-3xl space-y-4" data-testid="private-product-page"><PageTitle title={p.name} badge={<Tag>{t("cat.private_label")}</Tag>} />
      <Card><p className="text-sm">{p.function}</p><p className="mt-1 text-sm text-muted-foreground">{t("product.source")}: {p.source}</p></Card>
      <Notice>{t("cat.private_note")}</Notice>
      {p.submission ? <Notice testid="submission-status">{t(`cat.sub_${p.submission.status}`)} · {fmtDate(p.submission.created_at)}</Notice>
        : <Btn testid="submit-for-review-btn" onClick={() => api.post(`/products/${id}/submit`, {}).then(() => { toast.success(t("cat.submitted_ok")); reload(); }).catch(apiFail(t))}>{t("cat.submit_review")}</Btn>}
    </div>
  );
  return (
    <div className="mx-auto max-w-3xl space-y-6"><PageTitle title={p.name} sub={p.brand} /><ProductCard p={p} linkBase="/products" /><MyUse id={id} /></div>
  );
}
